import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

export function localIsoDate(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function addDaysIso(iso, days) {
  const [y, m, d] = String(iso).split("-").map(Number);
  return localIsoDate(new Date(y, m - 1, d + days));
}

export function BookingSlotCalendar({ days, selected, onSelect, loading, totalSlots, compact }) {
  const today = localIsoDate();
  const dayMap = useMemo(() => {
    const m = {};
    (days || []).forEach((d) => { m[d.date] = d; });
    return m;
  }, [days]);

  const openDates = useMemo(
    () => (days || []).filter((d) => (d.available_slots || 0) > 0).map((d) => d.date),
    [days]
  );

  const [cursor, setCursor] = useState(() => {
    const first = openDates[0] || today;
    const [y, m] = first.split("-").map(Number);
    return new Date(y, m - 1, 1);
  });

  useEffect(() => {
    if (!openDates.length) return;
    setCursor((prev) => {
      const visible = `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, "0")}`;
      if (openDates.some((iso) => iso.startsWith(visible))) return prev;
      const [y, m] = openDates[0].split("-").map(Number);
      return new Date(y, m - 1, 1);
    });
  }, [openDates]);

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const firstWeekday = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const monthLabel = cursor.toLocaleString("en-IN", { month: "long", year: "numeric" });
  const thisMonthKey = `${year}-${String(month + 1).padStart(2, "0")}`;
  const firstOpenMonth = (openDates[0] || today).slice(0, 7);
  const lastOpenMonth = (openDates[openDates.length - 1] || today).slice(0, 7);
  const canPrev = thisMonthKey > firstOpenMonth;
  const canNext = thisMonthKey < lastOpenMonth;

  const cells = [];
  for (let i = 0; i < firstWeekday; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) {
    const iso = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    cells.push({ iso, d, info: dayMap[iso] });
  }

  return (
    <div className="rounded-2xl border border-[#e5e7eb] p-3" data-testid="booking-date">
      <div className="flex items-center justify-between mb-3">
        <button type="button" disabled={!canPrev} onClick={() => setCursor(new Date(year, month - 1, 1))}
          className="p-1.5 rounded-full border border-[#e5e7eb] disabled:opacity-30" aria-label="Previous month">
          <ChevronLeft className="w-4 h-4 text-[#673c96]" />
        </button>
        <div className="text-center">
          <p className="text-sm font-semibold text-[#311b52]">{monthLabel}</p>
          <p className="text-[11px] text-[#673c96]">
            {loading ? "Loading dates…" : compact ? "Tap a highlighted date" : `${totalSlots || 0} slot${totalSlots === 1 ? "" : "s"} available`}
          </p>
        </div>
        <button type="button" disabled={!canNext} onClick={() => setCursor(new Date(year, month + 1, 1))}
          className="p-1.5 rounded-full border border-[#e5e7eb] disabled:opacity-30" aria-label="Next month">
          <ChevronRight className="w-4 h-4 text-[#673c96]" />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-[10px] font-semibold text-[#673c96]/70 uppercase mb-1.5">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((w) => (
          <div key={w} className="text-center">{w}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((c, idx) => {
          if (!c) return <div key={`pad-${idx}`} />;
          const count = c.info?.available_slots || 0;
          const past = c.iso < today;
          const open = !past && count > 0;
          const isSel = selected === c.iso;
          return (
            <button
              key={c.iso}
              type="button"
              disabled={!open}
              onClick={() => onSelect(c.iso)}
              className={`rounded-xl py-1.5 ${compact ? "min-h-[2.5rem]" : "min-h-[3.25rem]"} flex flex-col items-center justify-center border transition-colors ${
                isSel ? "bg-[#f37721] text-white border-[#f37721]"
                  : past ? "bg-[#ececef] text-[#c5c5c8] border-transparent cursor-not-allowed"
                  : open ? "bg-emerald-50 text-[#311b52] border-emerald-200 hover:border-[#673c96]/50"
                    : "bg-[#f7f7f9] text-[#d1d5db] border-transparent cursor-not-allowed"
              }`}
              data-testid={`booking-day-${c.iso}`}
            >
              {open && !compact ? (
                <>
                  <span className="text-sm font-semibold">{c.d}</span>
                  <span className={`text-[9px] font-bold leading-tight ${isSel ? "text-white" : "text-emerald-700"}`}>
                    {count} slot{count === 1 ? "" : "s"}
                  </span>
                </>
              ) : (
                <span className={`text-sm ${open ? "font-semibold" : ""}`}>{c.d}</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
